import React from 'react';
import CgEmptyState from './CgEmptyState';
import { useTranslation } from 'react-i18next';

/**
 * CgNoResults — backward-compatibility adapter over {@link CgEmptyState}.
 *
 * The empty-state grammar is now owned by CgEmptyState (three intents,
 * doodle-free). This shim keeps the legacy `variant` API working for existing
 * table call-sites while mapping each variant onto the new component, so the
 * old doodle illustrations and search-oriented placeholder copy are retired
 * everywhere in one move. Prefer importing CgEmptyState directly for new code.
 *
 * @param {Object} props
 * @param {boolean} [props.hasArrow] Legacy; ignored (doodles retired).
 * @param {'default'|'dataEntry'|'report'|'info'} [props.variant='default']
 * @param {Function} [props.onImport]
 * @param {Function} [props.onCreate]
 * @param {Function} [props.onGenerateReport]
 * @param {string} [props.createLabel]
 * @param {string} [props.importLabel]
 * @param {string} [props.title]
 * @param {string} [props.description]
 * @param {Function} [props.onNavigate]
 * @param {string} [props.navigateLabel]
 */
const CgNoResults = ({
  variant = 'default',
  onImport,
  onCreate,
  onGenerateReport,
  createLabel,
  importLabel,
  title,
  description,
  onNavigate,
  navigateLabel,
}) => {
  const { t } = useTranslation('common');
  const nothingYet = t('emptyState.nothingYet', 'Nothing here yet');
  const navigateText = navigateLabel ?? t('emptyState.goToImports', 'Go to imports');
  if (variant === 'dataEntry') {
    return (
      <CgEmptyState
        intent="first-run"
        title={title || nothingYet}
        description={
          description ||
          t(
            'emptyState.dataEntryHint',
            'Import your data in bulk or create an entry to get started.',
          )
        }
        onCreate={onCreate}
        createLabel={createLabel}
        onImport={onImport}
        importLabel={importLabel}
      />
    );
  }

  if (variant === 'report') {
    return (
      <CgEmptyState
        intent="first-run"
        icon="faFileAlt"
        title={title || t('emptyState.noReports', 'No reports yet')}
        description={
          description || t('emptyState.reportHint', 'Generate your first report to get started')
        }
        onAction={onGenerateReport}
        actionLabel={t('emptyState.generateReport', 'Generate report')}
        actionIcon="faFileAlt"
      />
    );
  }

  if (variant === 'info') {
    // With a navigation handler this is a first-run "go do X first" prompt;
    // without one it is a compact, muted section placeholder.
    if (onNavigate) {
      return (
        <CgEmptyState
          intent="first-run"
          title={title}
          description={description}
          onAction={onNavigate}
          actionLabel={navigateText}
          actionIcon="faArrowRight"
        />
      );
    }
    return (
      <CgEmptyState
        intent="empty-section"
        title={title}
        description={description}
      />
    );
  }

  // Legacy `default` — the old bare fallback. Callers that still pass
  // create/import handlers get a first-run; the rest get a neutral,
  // doodle-free section placeholder instead of the retired search copy.
  if (onCreate || onImport) {
    return (
      <CgEmptyState
        intent="first-run"
        title={title || nothingYet}
        description={description}
        onCreate={onCreate}
        createLabel={createLabel}
        onImport={onImport}
        importLabel={importLabel}
      />
    );
  }

  return (
    <CgEmptyState
      intent="empty-section"
      title={title || t('emptyState.noResults', 'No results')}
      description={description}
    />
  );
};

export default CgNoResults;
